/**
 * TenantSettingsModal: a tenant's scan schedule (how often Guardian scans
 * it, and whether scheduled scans run at all), on the Carbon Modal: a pinned
 * header with the tenant's name, a radio group, a toggle, a neutral note on
 * what the schedule means, and Cancel / Save settings in the footer.
 *
 * Opened from a tenant row on /tenants ("Tenant settings"). The Modal is a
 * headless UI Dialog, so it carries dialog semantics, traps focus and closes
 * on Escape.
 */
import { useState, useEffect } from 'react'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Modal } from '@/components/carbon/Modal'
import { RadioGroup } from '@/components/carbon/RadioGroup'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Toggle } from '@/components/carbon/Toggle'

interface TenantSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  tenant: {
    tenantId: string
    name: string
    domain?: string
  }
}

interface ScanSettings {
  scanFrequency: string
  scanScheduleEnabled: boolean
}

const FREQUENCIES = [
  { value: 'Manual', label: 'Manual', helper: 'Only when someone starts a scan.' },
  { value: 'Daily', label: 'Daily', helper: 'Once a day.' },
  { value: 'Weekly', label: 'Weekly', helper: 'Once a week.' },
]

export default function TenantSettingsModal({ isOpen, onClose, tenant }: TenantSettingsModalProps) {
  const [settings, setSettings] = useState<ScanSettings>({
    scanFrequency: 'Manual',
    scanScheduleEnabled: false
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load current settings when modal opens
  useEffect(() => {
    if (isOpen && tenant.tenantId) {
      loadSettings()
    }
  }, [isOpen, tenant.tenantId])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const currentSettings = await api.getTenantScanSettings(tenant.tenantId)
      setSettings({
        scanFrequency: currentSettings.scanFrequency,
        scanScheduleEnabled: currentSettings.scanScheduleEnabled
      })
    } catch (err) {
      console.error('Failed to load scan settings:', err)
      setError('Settings did not load. Refresh to try again.')
      // Set defaults on error
      setSettings({
        scanFrequency: 'Manual',
        scanScheduleEnabled: false
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      await api.updateTenantScanSettings(tenant.tenantId, settings)

      toast.success(`Scan settings updated for ${tenant.name}`)
      onClose()
    } catch (err: any) {
      console.error('Failed to save scan settings:', err)
      const errorMessage = err?.response?.data?.message || err?.message || 'Settings did not save. Try again.'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleFrequencyChange = (frequency: string) => {
    setSettings(prev => ({
      ...prev,
      scanFrequency: frequency
    }))
  }

  const handleScheduleToggle = (enabled: boolean) => {
    setSettings(prev => ({
      ...prev,
      scanScheduleEnabled: enabled
    }))
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      title="Scan settings"
      subtitle={tenant.name}
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: saving }}
      primaryAction={{ label: 'Save settings', onClick: handleSave, disabled: loading, loading: saving }}
    >
      <div className="flex flex-col gap-6">
        {error && <InlineNotification kind="error" title={error} />}

        {loading ? (
          <Skeleton lines={4} label="Loading settings." />
        ) : (
          <>
            <RadioGroup
              name="scanFrequency"
              legend="Scan frequency"
              value={settings.scanFrequency}
              onChange={handleFrequencyChange}
              options={FREQUENCIES}
            />

            <Toggle
              label="Automatic scanning"
              helper="Scheduled scans follow the frequency above."
              checked={settings.scanScheduleEnabled}
              onChange={handleScheduleToggle}
            />

            <InlineNotification kind="info" title="Scan schedule.">
              {settings.scanScheduleEnabled ? (
                <>
                  Automatic {settings.scanFrequency.toLowerCase()} scans are{' '}
                  <b className="font-semibold text-text-primary">on</b> for this tenant.
                </>
              ) : (
                <>
                  Automatic scanning is <b className="font-semibold text-text-primary">off</b>. Scans run only when
                  someone starts one.
                </>
              )}
            </InlineNotification>
          </>
        )}
      </div>
    </Modal>
  )
}
